import type { ReactNode } from "react";
import { inline } from "../_components/Prose";
import { Chev } from "./Chev";
import type { Field, Shape } from "../_lib/reference/schema";

/**
 * A field the way a developer scans it (T13e-2b): one line with its name, its type, whether it is required,
 * and its plain words; opened, the words in full and the details (default, limits, shapes, an example), and
 * the fields inside it, each one line again.
 */
export function Fld({
  id,
  name,
  type,
  required,
  absent,
  description,
  details,
  extra,
  kids,
}: {
  id?: string;
  name: string;
  type: string;
  required?: boolean;
  /** In what the API sends, a field that may be missing says so. */
  absent?: boolean;
  description: string | null;
  details: ReadonlyArray<readonly [ReactNode, ReactNode]>;
  /** Shown under the details: a field of the file's example, as the file writes it. */
  extra?: ReactNode;
  kids?: ReactNode;
}) {
  // A field with nothing more to say still says its type when opened.
  const rows: ReadonlyArray<readonly [ReactNode, ReactNode]> =
    details.length > 0
      ? details
      : kids || extra
        ? []
        : [["Type", <code key="t">{type}</code>]];
  return (
    <details className="fld" id={id}>
      <summary>
        <span className="fn">{name}</span>
        <span className="ft">{type}</span>
        {required ? <span className="fr">required</span> : null}
        {absent ? <span className="fo">may be absent</span> : null}
        {description ? <span className="fw">{inline(description)}</span> : null}
        <Chev />
      </summary>
      {rows.length > 0 ? (
        <dl className="fd">
          {rows.map(([dt, dd], i) => (
            <div key={i}>
              <dt>{dt}</dt>
              <dd>{dd}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {extra}
      {kids ? <div className="kids">{kids}</div> : null}
    </details>
  );
}

const firstUpper = (s: string): string => `${s[0]!.toUpperCase()}${s.slice(1)}`;
const exampleOf = (v: unknown): string =>
  typeof v === "string" ? v : JSON.stringify(v);

export function detailsOf(f: {
  default: string | null;
  limits: string | null;
  shapes?: string[][] | null;
  examples: unknown[] | null;
}): Array<readonly [ReactNode, ReactNode]> {
  const out: Array<readonly [ReactNode, ReactNode]> = [];
  if (f.default !== null)
    out.push([
      "Default",
      f.default === "" ? "Empty." : <code>{f.default}</code>,
    ]);
  if (f.limits) out.push(["Limits", `${firstUpper(f.limits)}.`]);
  if (f.shapes)
    out.push([
      "One of",
      f.shapes.map((keys, i) => (
        <span key={i}>
          {i > 0 ? ", " : null}
          <code>{`{ ${keys.join(", ")} }`}</code>
        </span>
      )),
    ]);
  if (f.examples?.length)
    out.push(["Example", <code key="e">{exampleOf(f.examples[0])}</code>]);
  return out;
}

interface Node {
  field: Field;
  kids: Node[];
}

/** The flat list the contract is read into (depth first, each field's own right after it), as a tree. */
function treeOf(fields: readonly Field[]): Node[] {
  const roots: Node[] = [];
  const stack: Node[] = [];
  for (const field of fields) {
    const node: Node = { field, kids: [] };
    while (
      stack.length > 0 &&
      stack[stack.length - 1]!.field.depth >= field.depth
    )
      stack.pop();
    if (stack.length === 0) roots.push(node);
    else stack[stack.length - 1]!.kids.push(node);
    stack.push(node);
  }
  return roots;
}

function FieldNode({ node, sent }: { node: Node; sent: boolean }) {
  const f = node.field;
  return (
    <Fld
      name={f.name}
      type={f.type}
      required={!sent && f.required}
      absent={sent && !f.required}
      description={f.description}
      details={detailsOf(f)}
      kids={
        node.kids.length > 0
          ? node.kids.map((k) => (
              <FieldNode key={k.field.path} node={k} sent={sent} />
            ))
          : undefined
      }
    />
  );
}

/** Fields, one line each. In what the API sends (`sent`), nearly every field is always there: the rare one that may be absent says so. */
export function FieldList({
  fields,
  sent = false,
}: {
  fields: readonly Field[];
  sent?: boolean;
}) {
  return (
    <div className="flds">
      {treeOf(fields).map((n) => (
        <FieldNode key={n.field.path} node={n} sent={sent} />
      ))}
    </div>
  );
}

/** A body or an answer: its fields, the shapes it may take, or the one value it is. */
export function ShapeList({
  shape,
  sent = false,
}: {
  shape: Shape;
  sent?: boolean;
}) {
  if (shape.kind === "fields")
    return <FieldList fields={shape.fields} sent={sent} />;
  if (shape.kind === "value")
    return (
      <p className="fval">
        <code>{shape.type}</code>
        {shape.description ? <> {inline(shape.description)}</> : null}
      </p>
    );
  return (
    <>
      {shape.shapes.map((s, i) => (
        <div key={s.keys.join(",")} className="either">
          <p className="sub">
            {i === 0 ? "Either" : "Or"}{" "}
            <code>{`{ ${s.keys.join(", ")} }`}</code>
          </p>
          <FieldList fields={s.fields} sent={sent} />
        </div>
      ))}
    </>
  );
}
